import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2, AlertCircle } from "lucide-react";
import Seo from "@/components/Seo";
import { SiteButton } from "@/components/brand/SiteButton";
import { Container, Section } from "@/components/layout/Section";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { SITE } from "@/config/site";
import { ROUTE_SEO } from "@/config/seoData";

const reveal = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

const Reveal = ({ children, className, testid }) => (
  <motion.div
    variants={reveal}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-60px" }}
    className={className}
    data-testid={testid}
  >
    {children}
  </motion.div>
);

const H2 = ({ children }) => (
  <h2 className="font-display text-2xl font-bold tracking-tight text-brand-snow sm:text-3xl">{children}</h2>
);

const EyebrowRow = ({ children }) => <p className="eyebrow">{children}</p>;

const ServiceDetail = () => {
  const { category: categorySlug, service: serviceSlug } = useParams();
  const [service, setService] = useState(null);
  const [category, setCategory] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    setService(null);
    api
      .get(`/services/${serviceSlug}`)
      .then((r) => {
        setService(r.data);
        setState("ok");
        api.get(`/services/categories/${r.data.category}`).then((c) => setCategory(c.data)).catch(() => {});
      })
      .catch(() => setState("error"));
  }, [serviceSlug]);

  const canonicalPath = service ? `/services/${service.category}/${service.slug}` : undefined;

  const jsonLd = useMemo(() => {
    if (!service) return [];
    const url = `${SITE.domain}${canonicalPath}`;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: service.name,
        description: service.seo.description,
        url,
        provider: {
          "@type": "Organization",
          name: SITE.name,
          url: SITE.domain,
          email: SITE.email,
          telephone: SITE.phone,
        },
        areaServed: ["Australia", "India", "United States", "United Kingdom", "Canada"],
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE.domain },
          { "@type": "ListItem", position: 2, name: "Services", item: `${SITE.domain}/services` },
          {
            "@type": "ListItem",
            position: 3,
            name: category?.name || service.category,
            item: `${SITE.domain}/services/${service.category}`,
          },
          { "@type": "ListItem", position: 4, name: service.name, item: url },
        ],
      },
      ...(service.faqs?.length
        ? [
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: service.faqs.map((f) => ({
                "@type": "Question",
                name: f.question,
                acceptedAnswer: { "@type": "Answer", text: f.answer },
              })),
            },
          ]
        : []),
    ];
  }, [service, category, canonicalPath]);

  if (state === "loading") {
    return (
      <Section className="pt-40" testid="service-loading">
        <Container>
          <p className="text-sm text-brand-mist">Loading service…</p>
        </Container>
      </Section>
    );
  }

  if (state === "error") {
    return (
      <>
        <Seo {...ROUTE_SEO.serviceNotFound} />
        <Section className="flex min-h-[70vh] items-center pt-32" testid="service-not-found">
          <Container className="max-w-2xl space-y-6">
            <p className="eyebrow">Not available</p>
            <h1 className="font-display text-3xl font-bold text-brand-snow sm:text-4xl">
              This service isn't available
            </h1>
            <p className="text-base text-brand-mist">
              It may have been moved, renamed or unpublished. The full capability stack is one click away.
            </p>
            <SiteButton to="/services" testid="service-not-found-back">
              Explore all services
            </SiteButton>
          </Container>
        </Section>
      </>
    );
  }

  // Canonical URL enforcement: wrong category in the path redirects to the service's real URL.
  if (service.category !== categorySlug) {
    return <Navigate to={canonicalPath} replace />;
  }

  return (
    <>
      <Seo
        title={service.seo.title}
        description={service.seo.description}
        canonicalPath={canonicalPath}
        ogImage={`${SITE.domain}/og/service/${service.slug}.png`}
        ogType="website"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-brand-mist/10 pt-32 pb-14 sm:pt-40 sm:pb-20" data-testid="service-hero">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(46rem 24rem at 82% 8%, rgba(56,139,255,0.10), transparent 62%), radial-gradient(38rem 22rem at 8% 88%, rgba(22,214,154,0.08), transparent 60%)",
          }}
        />
        <Container className="relative space-y-8">
          <nav aria-label="Breadcrumb" data-testid="service-breadcrumbs">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-brand-mist sm:text-sm">
              <li><Link to="/" className="transition-colors hover:text-brand-emerald" data-testid="crumb-home">Home</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to="/services" className="transition-colors hover:text-brand-emerald" data-testid="crumb-services">Services</Link></li>
              <li aria-hidden="true">/</li>
              <li>
                <Link to={`/services/${service.category}`} className="transition-colors hover:text-brand-emerald" data-testid="crumb-category">
                  {category?.name || "Category"}
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="text-brand-snow">{service.name}</li>
            </ol>
          </nav>
          <div className="max-w-3xl space-y-6">
            <motion.p variants={reveal} initial="hidden" animate="visible" className="eyebrow" data-testid="service-category-label">
              {category?.name || "Service"}
            </motion.p>
            <motion.h1
              variants={reveal}
              initial="hidden"
              animate="visible"
              className="font-display text-3xl font-bold leading-[1.08] tracking-tight text-brand-snow sm:text-4xl lg:text-5xl"
            >
              {service.name}
            </motion.h1>
            <motion.p variants={reveal} initial="hidden" animate="visible" className="text-base leading-relaxed text-brand-mist sm:text-lg" data-testid="service-intro">
              {service.intro}
            </motion.p>
            <motion.div variants={reveal} initial="hidden" animate="visible" className="flex flex-wrap gap-4" data-testid="service-hero-ctas">
              <SiteButton to={`/contact?service=${service.slug}`} testid="service-cta-primary" size="lg">
                {service.cta_text}
                <ArrowRight size={16} />
              </SiteButton>
              <SiteButton to={`/services/${service.category}`} variant="outline" testid="service-cta-secondary" size="lg">
                Explore {category?.name || "the category"}
              </SiteButton>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Overview + challenges */}
      <Section testid="service-overview-section">
        <Container className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="space-y-4 lg:col-span-6" testid="service-overview">
            <EyebrowRow>Overview</EyebrowRow>
            <H2>What this service covers</H2>
            <p className="text-base leading-relaxed text-brand-mist">{service.overview}</p>
          </Reveal>
          <Reveal className="lg:col-span-6" testid="service-challenges">
            <div className="surface-card h-full space-y-5 p-8">
              <EyebrowRow>The problems it solves</EyebrowRow>
              <ul className="space-y-4">
                {service.problems.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-brand-snow">
                    <AlertCircle size={16} className="mt-0.5 shrink-0 text-brand-azure" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* Capabilities + deliverables */}
      <Section testid="service-capabilities-section" className="bg-brand-charcoal/40">
        <Container className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="space-y-6 lg:col-span-6" testid="service-features">
            <EyebrowRow>Capabilities</EyebrowRow>
            <H2>What's inside</H2>
            <ul className="space-y-4">
              {service.features.map((f) => (
                <li key={f} className="flex items-start gap-3 border-b border-brand-mist/10 pb-4 text-sm leading-relaxed text-brand-snow">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-brand-emerald" />
                  {f}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-6" testid="service-deliverables">
            <div className="surface-card h-full space-y-5 p-8">
              <EyebrowRow>Scope & deliverables</EyebrowRow>
              <H2>What you receive</H2>
              <ul className="space-y-3">
                {service.deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm leading-relaxed text-brand-mist">
                    <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-emerald" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* Workflow + benefits */}
      <Section testid="service-workflow-section">
        <Container className="space-y-12">
          <Reveal className="max-w-2xl space-y-4" testid="service-workflow-heading">
            <EyebrowRow>How it runs</EyebrowRow>
            <H2>The engagement process</H2>
          </Reveal>
          <ol className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="service-workflow-steps">
            {service.workflow.map((step, i) => (
              <Reveal key={step.title} testid={`service-workflow-step-${i + 1}`}>
                <div className="surface-card h-full space-y-3 p-6">
                  <span className="font-display text-2xl font-bold text-brand-emerald/80">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-base font-semibold text-brand-snow">{step.title}</h3>
                  <p className="text-sm leading-relaxed text-brand-mist">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </ol>
          <Reveal testid="service-benefits">
            <div className="rounded-2xl border border-brand-emerald/20 bg-gradient-to-br from-brand-charcoal to-brand-black p-8 sm:p-10">
              <div className="grid gap-8 lg:grid-cols-12">
                <div className="space-y-4 lg:col-span-5">
                  <EyebrowRow>Why it matters</EyebrowRow>
                  <H2>The business case</H2>
                </div>
                <ul className="space-y-4 lg:col-span-7">
                  {service.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-brand-snow sm:text-base">
                      <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-brand-emerald" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* FAQs */}
      {service.faqs?.length > 0 && (
        <Section testid="service-faq-section" className="bg-brand-charcoal/40">
          <Container className="grid gap-10 lg:grid-cols-12 lg:gap-16">
            <Reveal className="space-y-4 lg:col-span-5" testid="service-faq-heading">
              <EyebrowRow>Questions</EyebrowRow>
              <H2>Asked before you ask</H2>
              <p className="text-sm leading-relaxed text-brand-mist sm:text-base">
                Straight answers to the questions every serious buyer asks about {service.name.toLowerCase()}.
              </p>
            </Reveal>
            <Reveal className="lg:col-span-7" testid="service-faq-accordion">
              <Accordion type="single" collapsible className="w-full">
                {service.faqs.map((f, i) => (
                  <AccordionItem key={f.question} value={`faq-${i}`} className="border-brand-mist/12">
                    <AccordionTrigger
                      data-testid={`faq-trigger-${i}`}
                      className="text-left font-display text-base font-semibold text-brand-snow hover:text-brand-emerald hover:no-underline"
                    >
                      {f.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-brand-mist" data-testid={`faq-content-${i}`}>
                      {f.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </Container>
        </Section>
      )}

      {/* Related services */}
      {service.related_services?.length > 0 && (
        <Section testid="service-related-section">
          <Container className="space-y-10">
            <Reveal className="max-w-2xl space-y-4" testid="service-related-heading">
              <EyebrowRow>Keep exploring</EyebrowRow>
              <H2>Related services</H2>
            </Reveal>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="service-related-grid">
              {service.related_services.map((r) => (
                <Reveal key={r.slug} testid={`related-card-${r.slug}`}>
                  <Link
                    to={`/services/${r.category}/${r.slug}`}
                    data-testid={`related-link-${r.slug}`}
                    className="group flex h-full flex-col justify-between gap-6 rounded-xl border border-brand-mist/12 bg-brand-charcoal p-6 transition-colors duration-200 hover:border-brand-emerald/35"
                  >
                    <div className="space-y-3">
                      <h3 className="font-display text-lg font-semibold text-brand-snow">{r.name}</h3>
                      <p className="text-sm leading-relaxed text-brand-mist">{r.intro}</p>
                    </div>
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-brand-emerald">
                      View service
                      <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </Container>
        </Section>
      )}

      {/* Final conversion */}
      <Section testid="service-final-cta" className="border-t border-brand-mist/10">
        <Container>
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl border border-brand-emerald/20 bg-gradient-to-br from-brand-charcoal via-brand-charcoal to-brand-black p-10 sm:p-14">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "radial-gradient(30rem 16rem at 88% 0%, rgba(56,139,255,0.12), transparent 60%), radial-gradient(26rem 14rem at 0% 100%, rgba(22,214,154,0.10), transparent 60%)",
                }}
              />
              <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
                <div className="max-w-xl space-y-4">
                  <EyebrowRow>Ready when you are</EyebrowRow>
                  <h2 className="font-display text-2xl font-bold tracking-tight text-brand-snow sm:text-3xl">
                    Talk to us about {service.name.toLowerCase()}
                  </h2>
                  <p className="text-sm leading-relaxed text-brand-mist sm:text-base">
                    A straight conversation about scope, approach and fit — with a senior engineer or strategist,
                    not a sales script.
                  </p>
                </div>
                <div className="flex shrink-0 flex-col gap-4 sm:flex-row lg:flex-col">
                  <SiteButton to={`/contact?service=${service.slug}`} testid="service-final-cta-primary" size="lg">
                    {service.cta_text}
                    <ArrowRight size={16} />
                  </SiteButton>
                  <SiteButton href={`mailto:${SITE.email}?subject=${encodeURIComponent(`Enquiry: ${service.name}`)}`} variant="outline" testid="service-final-cta-email" size="lg">
                    Email {SITE.email}
                  </SiteButton>
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </Section>
    </>
  );
};

export default ServiceDetail;
